import { commonQueryKeys } from '@/common/queryKeys/common';
import type { ScenarioItemFilters } from '@/features/planner/api/adaptive-planner/scenarioItemFilters';

export const plannerQueryKeys = {
  liveTimelineItems: {
    all: () => [...commonQueryKeys.itemAll(), 'planner-live-timeline'],
    page: ({ loopId, itemIds }: { loopId: number; itemIds: readonly number[] }) => [
      ...plannerQueryKeys.liveTimelineItems.all(),
      loopId,
      [...itemIds].sort((a, b) => a - b).join(','),
    ],
  },
  liveTimelineMetrics: {
    all: () => ['loop-planner', 'live-timeline-metrics'],
    detail: ({ loopId }: { loopId: number }) => [
      ...plannerQueryKeys.liveTimelineMetrics.all(),
      loopId,
    ],
  },
  sandbox: {
    current: ({ loopId }: { loopId: number }) => ['loop-planner', 'sandbox', loopId],
  },
  scenarios: {
    list: ({ loopId }: { loopId: number }) => ['loop-planner', 'scenarios', loopId],
    detail: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      'loop-planner',
      'scenarios',
      loopId,
      scenarioId,
    ],
    operation: ({
      loopId,
      scenarioId,
      operationId,
    }: {
      loopId: number;
      scenarioId: number;
      operationId: number;
    }) => ['loop-planner', 'scenario-operations', loopId, scenarioId, operationId],
  },
  scenarioAlerts: {
    root: () => ['loop-planner', 'scenario-alerts'],
    allAlerts: ({ scenarioId }: { scenarioId: number }) => [
      ...plannerQueryKeys.scenarioAlerts.root(),
      scenarioId,
    ],
    alerts: ({
      scenarioId,
      loopId,
      alertTypes,
    }: {
      scenarioId: number;
      loopId: number;
      alertTypes?: string[];
    }) => [...plannerQueryKeys.scenarioAlerts.allAlerts({ scenarioId }), loopId, alertTypes],
  },
  scenarioChangeSummary: {
    root: ({ scenarioId }: { scenarioId: number }) => [
      'loop-planner',
      'scenario-change-summary',
      scenarioId,
    ],
    detail: ({ scenarioId, loopId }: { scenarioId: number; loopId: number }) => [
      ...plannerQueryKeys.scenarioChangeSummary.root({ scenarioId }),
      loopId,
    ],
  },
  scenarioImpactSummary: {
    root: ({ scenarioId }: { scenarioId: number }) => [
      'loop-planner',
      'scenario-impact-summary',
      scenarioId,
    ],
    detail: ({
      scenarioId,
      loopId,
      sortBy,
    }: {
      scenarioId: number;
      loopId: number;
      sortBy?: string;
    }) => [...plannerQueryKeys.scenarioImpactSummary.root({ scenarioId }), loopId, sortBy],
  },
  scenarioImpactList: {
    root: ({ scenarioId }: { scenarioId: number }) => [
      'loop-planner',
      'scenario-impact-list',
      scenarioId,
    ],
    detail: ({
      scenarioId,
      loopId,
      list,
      direction,
    }: {
      scenarioId: number;
      loopId: number;
      list: string;
      direction: string;
    }) => [...plannerQueryKeys.scenarioImpactList.root({ scenarioId }), loopId, list, direction],
  },
  aiSuggestions: {
    root: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      'loop-planner',
      loopId,
      'scenario',
      scenarioId,
      'ai-suggestions',
    ],
    list: ({
      loopId,
      scenarioId,
      limit,
    }: {
      loopId: number;
      scenarioId: number;
      limit: number;
    }) => [...plannerQueryKeys.aiSuggestions.root({ loopId, scenarioId }), 'list', limit],
  },
  timelineItems: {
    root: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      'loop-planner',
      loopId,
      'scenario',
      scenarioId,
      'timeline-items',
    ],
    dataSetPrefix: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      ...plannerQueryKeys.timelineItems.root({ loopId, scenarioId }),
      'data-set',
    ],
    dataSet: ({
      loopId,
      scenarioId,
      filters,
      order,
    }: {
      loopId: number;
      scenarioId: number;
      filters?: ScenarioItemFilters;
      order?: string;
    }) => [
      ...plannerQueryKeys.timelineItems.dataSetPrefix({ loopId, scenarioId }),
      filters ?? {},
      order ?? null,
    ],
    imputedCount: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      ...plannerQueryKeys.timelineItems.root({ loopId, scenarioId }),
      'count',
      'imputed',
    ],
    bulkFetch: ({
      loopId,
      scenarioId,
      itemIds,
    }: {
      loopId: number;
      scenarioId: number;
      itemIds: number[];
    }) => {
      const sortedItemIds = [...itemIds].sort((a, b) => a - b);
      return [
        ...plannerQueryKeys.timelineItems.root({ loopId, scenarioId }),
        'bulk-fetch',
        sortedItemIds.join(','),
      ];
    },
  },
  schedules: {
    root: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      'loop-planner',
      loopId,
      'scenario',
      scenarioId,
      'schedules',
    ],
    bulkFetch: ({
      loopId,
      scenarioId,
      itemIds,
    }: {
      loopId: number;
      scenarioId: number;
      itemIds: number[];
    }) => {
      const sortedItemIds = [...itemIds].sort((a, b) => a - b);
      return [
        ...plannerQueryKeys.schedules.root({ loopId, scenarioId }),
        'bulk-fetch',
        sortedItemIds.join(','),
      ];
    },
  },
  scenarioItems: {
    root: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      'loop-planner',
      loopId,
      'scenario',
      scenarioId,
      'scenario-items',
    ],
    hasExternalItems: ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => [
      ...plannerQueryKeys.scenarioItems.root({ loopId, scenarioId }),
      'has-external-items',
    ],
    detail: ({
      loopId,
      scenarioId,
      itemId,
    }: {
      loopId: number;
      scenarioId: number;
      itemId: number;
    }) => [...plannerQueryKeys.scenarioItems.root({ loopId, scenarioId }), itemId],
    ancestors: ({
      loopId,
      scenarioId,
      itemId,
    }: {
      loopId: number;
      scenarioId: number;
      itemId: number;
    }) => [...plannerQueryKeys.scenarioItems.detail({ loopId, scenarioId, itemId }), 'ancestors'],
  },
};
